import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Alert, Box } from '@zillow/constellation-10';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { PaymentGatewayContext } from '../../PaymentGateway/context';
import { RMWContext } from '../../../providers/RMWContext';

import { useSelector } from '../../../selectors/useSelector';
import PropertiesActions from '../../../actions/PropertiesActions';
import actions from '../../../containers/CreateListingPage/actions';
import { default as pgActions } from '../../PaymentGateway/actions';
import { RMW_SUB_PATH } from '../../../pathsManifest';

import PropertyConstants from '../../../constants/PropertyConstants';
import { DISPLAY_CONTEXT } from '../../PaymentGateway/constants';
import { EXIT_CTA_LABEL } from '../../../constants/WizardConstants';

import { getFreemiumProductByType } from '../../../utils/upsellUtils';

import PayAndPublishCSEvents from '../../../model/ClickstreamEvent/PayAndPublishCSEvents';

import PageWizard from '../../Wizard/PageWizard';
import SpinnerContainer from '../../SpinnerContainer';
import PreActivationModalWrapper from '../../PreActivationModalWrapper';

import FreemiumPay from './components/FreemiumPay';

import { componentStyles } from '../Review/styles';

import type { ChargeAndActivateListingProps } from '../../../model/ChargeAndActivateListing';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

export type PayAndPublishProps = {
  onGoToPrevStep: () => void;
  onGoToNextStep?: () => void;
  onExitWizard: () => void;
  wizardTitle: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const PayAndPublish = ({
  onGoToNextStep = () => {},
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: PayAndPublishProps) => {
  const dispatch = useDispatch();

  const {
    pgState: { isResponseSuccess, responseData },
    reducerDispatch: pgReducerDispatch,
  } = useContext(PaymentGatewayContext);

  const pgResponseData = responseData as {
    expirationDateTimestamp: number;
    activeProductType: number;
  };

  const {
    modularListingState: {
      // @ts-expect-error
      serverErrorObj: { saveAndExitError },
      isInReactivationFlow,
    },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { paths } = useContext(RMWContext);

  const { currentProperty, active, isFreemiumEnabled, freemiumProducts, listingAlias, singleFamilyContract, user } =
    useSelector((state) => {
      return {
        currentProperty: state.properties.currentProperty,
        active: state.properties.currentProperty.active,
        isFreemiumEnabled: state.properties.currentProperty.freemium.isEnabled,
        freemiumProducts: state.properties.currentProperty.freemium.products,
        listingAlias: state.properties.currentProperty.listingAlias,
        singleFamilyContract: state.properties.currentProperty.singleFamilyContract,
        user: state.user,
      };
    }, shallowEqual);

  const [isLoading, setIsLoading] = useState(true);
  const [showPreActivationModal, setShowPreActivationModal] = useState(false);

  useEffect(() => {
    if (active === true) {
      goDirectlyToSuccessPage();
      setIsLoading(false);
      return;
    }

    // Fallback in case the Review step failed
    if (singleFamilyContract?.isRequired) {
      setShowPreActivationModal(true);
      return;
    }

    if (isFreemiumEnabled === false) {
      autoActivate();
      return;
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    // IMPORTANT: listens for payment gateway response
    if (isResponseSuccess) {
      handleFreemiumActivateSuccess(pgResponseData);
    }
  }, [isResponseSuccess]);

  const nonFreemiumLabel = singleFamilyContract?.isRequired ? 'Next' : 'Continue to payment';

  const nextLabel = isFreemiumEnabled ? 'Go premium' : nonFreemiumLabel;

  const goDirectlyToSuccessPage = () => {
    handleWizardComplete();
  };

  const autoActivate = () => {
    updateDisplayPaymentGatewayOnApp(true);
  };

  const updateDisplayPaymentGatewayOnApp = (value: boolean) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: value ? DISPLAY_CONTEXT.MODULAR_LISTING : null,
      },
    });
  };

  const updateSelectedProductType = (selectedProductType: number) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handleActivateSuccess = (chargeAndActivateListingResponse: {
    success: boolean;
    response: ChargeAndActivateListingProps;
  }) => {
    setIsLoading(false);
    const {
      success,
      response: {
        activation: { daysExtended, expirationDateTimestamp },
      },
    } = chargeAndActivateListingResponse;

    if (!success) {
      return;
    }

    dispatch(PropertiesActions.updateCurrentProperty({ expirationDateTimestamp, daysExtended }));

    handleWizardComplete();
  };

  const handleFreemiumActivateSuccess = (response: { expirationDateTimestamp: number; activeProductType: number }) => {
    const { expirationDateTimestamp, activeProductType } = response;
    if (activeProductType) {
      const activeProduct = getFreemiumProductByType(freemiumProducts, activeProductType);
      if (activeProduct) {
        dispatch(PropertiesActions.updateCurrentPropertyFreemium({ activeProduct }));
      }
    }

    dispatch(PropertiesActions.updateCurrentProperty({ expirationDateTimestamp }));

    handleWizardComplete();
  };

  const handleWizardComplete = () => {
    actions.updateWizardComplete({ reducerDispatch, isWizardComplete: true });
    window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`);
  };

  const handleNextClick = () => {
    if (isFreemiumEnabled) {
      PayAndPublishCSEvents.StartActivatePremiumFooter({ currentProperty, user });
      updateSelectedProductType(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
      updateDisplayPaymentGatewayOnApp(true);
      return;
    }

    if (!isFreemiumEnabled) {
      updateDisplayPaymentGatewayOnApp(true);
      return;
    }

    onGoToNextStep();
  };

  return (
    <PageWizard
      {...rest}
      hideHeader={false}
      hideFooter={false}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      nextLabel={nextLabel}
      isNextDisabled={singleFamilyContract?.isRequired}
      onNextClick={handleNextClick}
      onNextDataEventId={PayAndPublishCSEvents.StartActivatePremiumFooter.data_event_id}
      onBackClick={onGoToPrevStep}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      isNarrowLayout={false}
      hideStepIndex={isInReactivationFlow}
      hideBackButton={false}
    >
      <Box css={componentStyles} data-testid="PayAndPublish">
        {isLoading ? (
          <SpinnerContainer />
        ) : (
          <>
            {saveAndExitError && (
              <Alert tone="critical" css={{ marginBottom: '600' }}>
                {saveAndExitError}
              </Alert>
            )}
            <FreemiumPay />
          </>
        )}
      </Box>
      <PreActivationModalWrapper
        showPreActivationModal={showPreActivationModal}
        updateShowPreActivationModal={setShowPreActivationModal}
        onChargeAndActivateListingResponse={handleActivateSuccess}
      />
    </PageWizard>
  );
};

export default PayAndPublish;
